<template>
  <div>
    <b-loading v-model="isProgressExporting">
      <p>
        <TitleView>{{ $t("pdf-export") }}</TitleView>
        <b v-if="step === 1">{{ $t("pdf-export-step1") }}</b>
        <b v-else-if="step === 2">{{ $t("pdf-export-step2") }}</b>
        <b v-else-if="step === 3">{{ $t("pdf-export-step3") }}</b>
        <b v-else>{{ $t("pdf-export-step4") }}</b>
        <br /><br />
        <b-progress type="is-info" :value="progress" show-value></b-progress>
      </p>
    </b-loading>

    <div id="pdf-target" class="hidden">
      <div class="fond-bleu"></div>
      <div class="fond-vert"></div>
      <div class="fullsize">
        <div class="left">
          <div class="top-container">
            <img src="/img/poisson.svg" class="poisson" alt="poisson" />
            <div class="info">
              <div class="title">{{ project.nom }}</div>
              <div class="nature">{{ $t(`pdf-export-${project.natureAireEducative}`) }}</div>
              <div v-for="etablissement in project.etablissements" :key="etablissement.id">
                <span class="name">{{ etablissement.nom }}</span> ({{ etablissement.ville }})
              </div>

              <div>{{ $t("pdf-export-eleves", [totalEleves, niveauxLabel]) }}</div>

              <div v-if="project.structures && project.structures.length !== 0">
                <div v-for="structure in project.structures" :key="structure.id">
                  <span class="name">{{ structure.nom }}</span>
                </div>
              </div>

              <div>{{ $t("pdf-export-inscription", [anneeInscription]) }}</div>
            </div>
            <img src="/img/scarabee.svg" class="scarabee" alt="scarabée" />
          </div>

          <div class="pitch-map-container">
            <div>
              <p>{{ $t("pdf-export-annee", [project.anneeScolaire]) }}</p>
              <div class="pitch">{{ project.pitch }}</div>
            </div>

            <img
              v-if="withMap && mapDataUrl"
              :src="mapDataUrl"
              class="map"
              alt="carte"
              crossorigin="anonymous"
            />
          </div>
        </div>

        <div class="ate photo">
          <div>
            <img
              v-if="withPhotos && imagesUrl[1]"
              id="img-orig-1"
              :src="imagesUrl[1]"
              crossorigin="use-credentials"
              alt="Photo ATE"
              @load="convertToBase64"
            />
            <img
              v-else
              src="@/assets/images/ATE.png"
              alt="Logo Aire Terrestre Éducative"
              title="Logo Aire Terrestre Éducative"
            />
          </div>
          <p>{{ $t("pdf-export-photo1") }}</p>
        </div>

        <div class="ofb photo">
          <div class="photo-container">
            <img
              v-if="withPhotos && imagesUrl[0]"
              id="img-orig-0"
              :src="imagesUrl[0]"
              crossorigin="use-credentials"
              alt="Photo ofb"
              @load="convertToBase64"
            />
            <img
              v-else
              src="@/assets/images/ofb.png"
              alt="Logo Office Français pour la Biodiversité"
              title="Logo Office Français pour la Biodiversité"
            />
          </div>
          <p>{{ $t("pdf-export-photo2") }}</p>
        </div>

        <div class="ame photo">
          <div>
            <img
              v-if="withPhotos && imagesUrl[2]"
              id="img-orig-2"
              :src="imagesUrl[2]"
              crossorigin="use-credentials"
              alt="Photo AME"
              @load="convertToBase64"
            />
            <img
              v-else
              src="@/assets/images/AME.png"
              alt="Logo Aire Marine Éducative"
              title="Logo Aire Marine Éducative"
            />
          </div>
          <p>{{ $t("pdf-export-photo3") }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { NIVEAUX } from "@/model/bean/EnumHelper"
import { ProjetAnnuelDTO } from "@/model/bean/GeneratedDTOs"
import { InfoReportingService } from "@/services/log/InfoReportingService"
import { ProjectService } from "@/services/rest/project/ProjectService"
import TitleView from "@/views/commons/TitleView.vue"
import { BLoading, BProgress } from "buefy"
import html2pdf from "html2pdf.js"
import { defineComponent, PropType } from "vue"

export default defineComponent({
  name: "ProjectPDFExport",
  components: {
    TitleView,
    BLoading,
    BProgress,
  },
  props: {
    project: {
      type: Object as PropType<ProjetAnnuelDTO>,
      required: true,
    },
    exportingPdf: {
      type: Boolean,
      required: true,
    },
    withPhotos: {
      type: Boolean,
      required: true,
    },
    withMap: {
      type: Boolean,
      default: false,
      required: false,
    },
    mapDataUrl: {
      type: [String, null] as PropType<string | null>,
      default: null,
    },
    imagesUrl: {
      type: Array as PropType<string[]>,
      required: true,
    },
  },

  emits: ["export-done"],
  data() {
    return {
      // Services
      infoReportingService: InfoReportingService.INSTANCE,
      projetService: ProjectService.INSTANCE,

      // Component state
      isProgressExporting: false,
      step: 1,
      progress: 0,
      totalEleves: 0,
      niveauxLabel: "",
      anneeInscription: "",
      progressInterval: null as number | null,
    }
  },
  watch: {
    exportingPdf: {
      handler(): void {
        if (this.exportingPdf) {
          this.$nextTick(() => {
            this.startProgressInterval()
            this.generateReport()
          })
        } else {
          // Arrêter l'interval de progression quand l'export est terminé
          this.stopProgressInterval()
        }
      },
    },
    mapDataUrl: {
      handler(): void {
        this.$nextTick(() => {
          if (this.withMap) {
            this.generateReport()
          }
        })
      },
    },
  },

  async created(): Promise<void> {
    const periods = await this.projetService.getPeriods(this.project.projetId)
    const max = periods.length
    this.anneeInscription =
      new Date(periods[max - 1].anneeScolaireDebut).getFullYear() +
      "-" +
      new Date(periods[max - 1].anneeScolaireFin).getFullYear()
  },

  beforeUnmount(): void {
    // Nettoyer l'interval de progression lors de la destruction du composant
    this.stopProgressInterval()
  },
  methods: {
    async generateReport(): Promise<void> {
      if (this.isProgressExporting && (!this.withMap || (this.withMap && this.mapDataUrl))) {
        this.step = 2
        this.preparePDF()
        // Attendre que Vue mette à jour le DOM avec les nouvelles valeurs
        await this.$nextTick()
        this.step = 3
        this.exportPDF()
      }
    },

    preparePDF(): void {
      this.generateClasseStrings()
    },

    updateProgress(value: number): void {
      this.progress = value
      // Forcer la mise à jour de Vue
      this.$forceUpdate()
    },

    startProgressInterval(): void {
      this.isProgressExporting = true
      this.stopProgressInterval()
      this.progressInterval = window.setInterval(() => {
        if (this.progress < 98) {
          if (this.step === 2 && this.progress < 48) {
            this.progress = 48
          } else if (this.step === 3 && this.progress < 68) {
            this.progress = 68
          }
          this.progress += 2
        }
      }, 70)
    },

    stopProgressInterval(): void {
      this.step = 1
      this.progress = 0

      if (this.progressInterval !== null) {
        clearInterval(this.progressInterval)
        this.progressInterval = null
      }
    },

    generateClasseStrings(): void {
      if (!this.project.classes || this.project.classes.length === 0) {
        return
      }
      
      // Réinitialiser les compteurs
      this.totalEleves = 0
      this.niveauxLabel = ""
      
      const niveaux: string[] = []
      const autreLabels: string[] = []

      this.project.classes.forEach((classe) => {
        //@ts-ignore
        Object.entries(NIVEAUX).forEach(([niveau, niveauLabel]) => {
          //@ts-ignore
          const nbreEleve = classe[niveau]

          if (nbreEleve) {
            this.totalEleves += nbreEleve
            if (niveaux.indexOf(niveauLabel as string) === -1) {
              niveaux.push(niveauLabel)
            }
          }
        })
        if (classe.autre1) {
          this.totalEleves += classe.eleves_autre1
          if (autreLabels.indexOf(classe.autre1) === -1) {
            autreLabels.push(classe.autre1)
          }
        }

        if (classe.autre2) {
          this.totalEleves += classe.eleves_autre2
          if (autreLabels.indexOf(classe.autre2) === -1) {
            autreLabels.push(classe.autre2)
          }
        }
      })

      const niveauxLabels: string[] = []

      Object.values(NIVEAUX).forEach((niveauLabel) => {
        if (niveaux.indexOf(niveauLabel) !== -1) {
          niveauxLabels.push(
            this.$t(
              `projetPage.infoAdministratives.classe.niveauxLabels.${niveauLabel}`,
            ).toString(),
          )
        }
      })

      const allNiveauxLabels = [...niveauxLabels, ...autreLabels]

      const nbreNiveaux = allNiveauxLabels.length
      allNiveauxLabels.forEach((niveau, index) => {
        if (index === 0) {
          //@ts-ignore
          this.niveauxLabel = niveau
        } else if (index === nbreNiveaux - 1) {
          this.niveauxLabel += ` et ${niveau}`
        } else {
          this.niveauxLabel += `, ${niveau}`
        }
      })
    },

    async exportPDF(): Promise<void> {
      try {
        this.updateProgress(20)
        const dateString = new Date().toISOString().substring(0, 10)
        const aireType = this.project.natureAireEducative ? this.project.natureAireEducative : "AE"
        const pdfFileName =
          aireType +
          "_" +
          this.project.nom
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(" ", "_") +
          "_" +
          dateString +
          ".pdf"

        this.updateProgress(27)
        const element = document.getElementById("pdf-target")
        const clone = this.createPDFHTML(element as HTMLElement)

        this.updateProgress(40)
        // All options here
        // https://ekoopmans.github.io/html2pdf.js/#options
        const opt = {
          margin: 0,
          filename: pdfFileName,
          image: { type: "jpg", quality: 0.98 },
          pagebreak: { mode: ["avoid-all"], before: ".pdf-page-break" },
          html2canvas: {
            scale: 1.5,
            useCORS: true,
          },
          jsPDF: { unit: "in", format: "a4", orientation: "landscape" },
        }

        await html2pdf().from(clone).set(opt).toContainer().toCanvas().toImg().toPdf().save()
        this.updateProgress(100)
      } catch (exception) {
        console.error(exception)
        this.infoReportingService.error(this.$t("pdf-export-error").toString(), new Error())
      } finally {
        this.stopProgressInterval()
        this.isProgressExporting = false
      }

      await this.$router.push({
        query: {},
      })
      this.$emit("export-done", true)
    },

    createPDFHTML(original: HTMLElement): HTMLElement {
      const pdfClone = original?.cloneNode(true) as HTMLElement
      pdfClone.classList.remove("hidden")
      return pdfClone
    },

    convertToBase64(e: Event): void {
      // @ts-ignore
      const origin = e.currentTarget as HTMLImageElement
      const canvas = document.createElement("canvas")
      const dataURL = this.getDataUrl(origin, canvas)
      // @ts-ignore
      origin?.setAttribute("src", dataURL)
    },

    getDataUrl(img: HTMLImageElement, canvas: HTMLCanvasElement): string {
      // Create canvas
      // @ts-ignore
      const ctx = canvas.getContext("2d")

      // Utiliser les dimensions naturelles de l'image pour conserver les proportions
      const width = img.naturalWidth
      const height = img.naturalHeight

      canvas.width = width
      canvas.height = height

      // Draw the image complète sans recadrage
      //@ts-ignore
      ctx.drawImage(img, 0, 0, width, height)
      // @ts-ignore convert as base 64
      return canvas.toDataURL("image/jpeg")
    },
  },
})
</script>

<style lang="scss" scoped>
#pdf-target.hidden {
  display: none;
}

.nature {
  font-weight: bold;
  margin-bottom: 10px;
}

.title {
  font-size: 18px;
  font-weight: bold;
  padding-bottom: 0;
  margin-bottom: 8px;
}

.name {
  text-transform: uppercase;
}

.top-container {
  display: flex;
}

.pitch-map-container {
  display: flex;
  height: 550px;
}

.pitch {
  line-height: 18px;
  font-size: 14px;
  padding: 10px;
  border: 1px solid #dadada;
  border-radius: 10px;
  width: 450px;
  height: 527px;
  overflow: hidden;
}

.map {
  margin-left: 10px;
  margin-top: 0;
  width: 275px;
  height: 550px;
  border-radius: 10px;
}

.left {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin: 10px;
  padding: 10px;
  background: white;
  width: 753px;
  height: 753px;
}

.fullsize {
  margin-left: 10px;
  margin-top: 10px;
  padding-top: 1px;
  height: 778px;
  width: 1104px;
  background-image: url("/img/fondPDF.png");
  background-size: cover;
}

.poisson {
  height: 40px;
  width: 40px;
  align-self: flex-start;
  margin-right: 30px;
}

.scarabee {
  height: 40px;
  width: 40px;
  align-self: flex-end;
  margin-left: 30px;
}

.info {
  width: 593px;
  height: 173px;
}

.ame {
  left: 810px;
  top: 525px;

  transform: rotate(-4.47deg);
}

.ofb {
  left: 880px;
  top: 270px;

  transform: rotate(8.5deg);
}

.ate {
  left: 786px;
  top: 20px;

  transform: rotate(-8.2deg);
}

.photo {
  box-sizing: border-box;

  position: absolute;
  width: 215px;
  height: 250px;

  background: #ffffff;
  border: 0.5px solid #d8d8d8;
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.44);

  padding: 5px;

  div {
    box-sizing: border-box;
    width: 204px;
    height: 204px;

    border: 0.5px solid #b9b9b9;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    img {
      max-width: 100%;
      max-height: 100%;
      width: auto;
      height: auto;
      object-fit: contain;
    }
  }

  p {
    margin-top: 10px;
    font-family: "Satisfy", serif;
    font-style: normal;
    font-weight: 400;
    font-size: 15px;
    line-height: 120%;

    text-align: center;

    color: #000000;
  }
}
</style>
